import { useMutation, useQuery } from '@tanstack/react-query';
import { ReviewCreateDTO } from '@bitecritic-api/src/reviews/dto/review.dto';
import { reviewsService } from '~/services/reviewsService/reviews.service';
import { useQueryClient } from '@tanstack/react-query';
import { ReviewResponseDto } from '@bitecritic-api/src/reviews/dto/review.response.dto';
import { useLocale } from './useLocale';

export const useReview = (id?: string) => {
  const queryClient = useQueryClient();
  const { currentLocale } = useLocale();

  // Always fetch the review with the current locale language to get translations
  const requestLanguage = currentLocale.language;

  // Fetch the review with the language parameter to ensure we get translations
  const reviewQuery = useQuery<ReviewResponseDto>({
    queryKey: ['review', id, requestLanguage],
    queryFn: () => reviewsService.getReviewById(id!, requestLanguage),
    enabled: !!id,
  });

  const createReviewMutation = useMutation<
    ReviewResponseDto,
    Error,
    ReviewCreateDTO
  >({
    mutationFn: (review: ReviewCreateDTO) =>
      reviewsService.createReview(review),
    onSuccess: (data) => {
      queryClient.setQueryData(['review', data.id], data);
      // Invalidate reviews list queries to refetch with new data
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
    },
    onError: (error: Error) => {
      console.error('Error creating review:', error);
      throw error;
    },
  });

  return {
    review: reviewQuery.data,
    isLoading: reviewQuery.isLoading || createReviewMutation.isPending,
    error: reviewQuery.error || createReviewMutation.error,
    createReview: createReviewMutation.mutateAsync,
    isCreating: createReviewMutation.isPending,
    isError: reviewQuery.isError || createReviewMutation.isError,
    errorMessage:
      (reviewQuery.error || createReviewMutation.error)?.message || '',
  };
};
